import {makeAutoObservable, runInAction} from 'mobx';

import type {ProgramExpandedCommon} from '../../../../api/program';
import type {ApiCountResponse} from '../../../../api/types';
import type {CoreClientInvitesCountParams} from '../../../../api/types/core/methods';
import type {WithBusyManager} from '../../../../utils/store';

import {membershipStore} from '../../../../api/membership';
import {currentUserStore} from '../../../../api/user';
import {apiRequest} from '../../../../api/utils/apiRequest';
import {findFollowerByUserId} from '../../../../services/entity-action/follower/follower.find-by-user-id';
import {getProposedMembership} from '../../../../utils/entity/program/getProposedMembership';
import {logger} from '../../../../utils/logger';
import {AsyncBusyManager} from '../../../../utils/store';

type Config = {
  program: ProgramExpandedCommon;
};

type ReasonKey = 'fetchData';

export class JoinIndividualProgramStore implements WithBusyManager<ReasonKey> {
  busy = new AsyncBusyManager<ReasonKey>();
  isClientInvited = false;
  isClientMatched = false;

  constructor(private readonly config: Config) {
    this.busy.observe(this, {
      fetchData: 'fetchData',
    });

    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.fetchData();
  }

  get clientMembership() {
    return (
      Array.from(membershipStore.membershipLookup.values()).find(
        ({program_id, status}) =>
          this.config.program._id === program_id &&
          status !== 'archived' &&
          status !== 'proposed',
      ) ?? null
    );
  }

  get hasRequestBeenSent() {
    return Boolean(this.clientMembership?.status === 'requested');
  }

  get isClientAbleToJoin() {
    const {program} = this.config;

    if (this.isFetching) {
      return false;
    }

    if (program.status === 'active' && this.lastEdition) {
      if (this.isClientMatched) {
        return false;
      }

      if (program.is_closed) {
        return this.isClientInvited;
      }

      return (
        !this.clientMembership || this.clientMembership.status === 'proposed'
      );
    }

    return false;
  }

  get isFetching() {
    return this.busy.getReason('fetchData');
  }

  get lastEdition() {
    return this.config.program.editions[0];
  }

  async fetchData() {
    const follower = await this.fetchFollower();

    const isClientMatched = Boolean(
      follower?.is_matched && follower?.status !== 'invited',
    );

    const isClientInvited = follower
      ? await this.checkIsClientInvited()
      : false;

    runInAction(() => {
      this.isClientMatched = isClientMatched;
      this.isClientInvited = isClientInvited;
    });
  }

  private async checkIsClientInvited() {
    try {
      const proposedMembership = await getProposedMembership(
        this.config.program,
      );

      if (proposedMembership) {
        return true;
      }

      const invitesCountResult: ApiCountResponse =
        await apiRequest<CoreClientInvitesCountParams>({
          method: 'client.invites.count',
          params: {
            query: [
              ['program_id', '==', this.config.program._id],
              ['user_id', '==', currentUserStore.user!._id],
            ],
          },
        });

      return invitesCountResult._count > 0;
    } catch (e) {
      logger.error(e);

      return false;
    }
  }

  private fetchFollower() {
    try {
      return findFollowerByUserId({
        userId: this.config.program.user_id,
      });
    } catch (e) {
      return null;
    }
  }
}
